import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const SubscriptionPayU = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        // Get URL from query parameters
        const url = searchParams.get('url');
        const paymentType = searchParams.get('paymentType') || 'subscription';
        
        // Get user details from localStorage
        const parsedUserDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');

        if (!url) {
          setError('Payment URL not found');
          setIsRedirecting(false);
          return;
        }

        // Validate URL format
        if (!url.startsWith('http')) {
          setError('Invalid payment URL');
          setIsRedirecting(false);
          return;
        }

        // Store payment type for later use
        localStorage.setItem('paymentType', paymentType);

        // Add a small delay to show loading state
        setTimeout(() => {
          // Redirect to PayU payment URL
          window.location.href = url;
        }, 1000);

      } catch (error) {
        console.error('Payment initialization error:', error);
        setError('Failed to initialize payment');
        setIsRedirecting(false);
      }
    };

    initializePayment();
  }, [searchParams]);

  useEffect(() => {
    // Listen for payment status updates from PayU
    const handlePaymentResponse = (event) => {
      try {
        // Handle PayU response
        if (event.origin === window.location.origin && event.data?.paymentStatus) {
          handlePaymentStatus(event.data);
        }
      } catch (error) {
        console.error('Payment response handling error:', error);
      }
    };

    // Listen for URL changes (PayU redirects back)
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('status');
      const txnid = urlParams.get('txnid');
      const error = urlParams.get('error');

      if (status || txnid || error) {
        handlePaymentStatus({
          status: status || (error ? 'failure' : 'success'),
          trancationid: txnid,
          remarks: error || 'Payment completed'
        });
      }
    };

    window.addEventListener('message', handlePaymentResponse);
    window.addEventListener('popstate', handleUrlChange);
    
    // Check current URL for payment response
    handleUrlChange();
    
    return () => {
      window.removeEventListener('message', handlePaymentResponse);
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  const extractParamsFromURL = (url) => {
    try {
      const params = {};
      const urlObj = new URL(url);
      const queryString = urlObj.search;
      
      if (queryString) {
        const searchParams = new URLSearchParams(queryString);
        for (const [key, value] of searchParams.entries()) {
          params[key] = decodeURIComponent(value);
        }
      }
      return params;
    } catch (error) {
      console.error('Error extracting URL parameters:', error);
      return {};
    }
  };

  const handlePaymentStatus = async (responseData) => {
    try {
      const { status, trancationid, remarks } = responseData;

      // Store payment status in localStorage
      localStorage.setItem('paymentStatus', status);
      localStorage.setItem('trancationid', trancationid || '');
      localStorage.setItem('paymentRemarks', remarks || '');
      localStorage.setItem('txnProcessed', 'true');

      // Navigate based on payment status
      switch (status) {
        case 'success':
        case 'pending':
          navigate('/thankyoucaresathi', { replace: true });
          break;
        case 'failure':
        case 'cancel':
          navigate('/caresathi', { 
            state: { 
              paymentError: 'Payment failed. Please try again.',
              transactionId: trancationid 
            },
            replace: true 
          });
          break;
        default:
          navigate('/caresathi', { replace: true });
      }
    } catch (error) {
      console.error('Payment status handling error:', error);
      navigate('/caresathi', { replace: true });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/caresathi')}
            className="px-6 py-3 bg-[#004171] text-white rounded-lg hover:bg-[#003a5d] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show loading while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#004171] mx-auto"></div>
        <h2 className="mt-6 text-2xl font-bold text-gray-800">Redirecting to Payment Gateway</h2>
        <p className="mt-4 text-lg text-gray-600">
          Please wait while we redirect you to complete your payment securely.
        </p>
        <div className="mt-4 text-sm text-gray-500">
          <p>• Do not close this window</p>
          <p>• Complete the payment process</p>
          <p>• You will be redirected back automatically</p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPayU;